/**
 * The sidebar-mode register: `expanded` | `collapsed` | `hover`, persisted.
 *
 * The rail's pixel geometry lives ONLY in @latisent/brand/theme.css
 * (`--app-sidebar-expanded-w` / `--app-sidebar-collapsed-w`), and no JS may
 * write a width. Both the pre-paint script in index.html and the React effect
 * in AppSidebar set only `data-sidebar-mode` on <html>; the tokens derive
 * `--app-content-inset` from it. Do not reintroduce a px literal.
 */

export type SidebarMode = 'expanded' | 'collapsed' | 'hover';

/** Keep in lockstep with the pre-paint script in index.html. */
export const CONSOLE_SIDEBAR_MODE_KEY = 'latisent-sidebar-mode';

function isSidebarMode(value: unknown): value is SidebarMode {
  return value === 'expanded' || value === 'collapsed' || value === 'hover';
}

/** Read the saved sidebar mode from localStorage. */
export function readSidebarMode(
  storageKey = CONSOLE_SIDEBAR_MODE_KEY,
  defaultMode: SidebarMode = 'expanded',
): SidebarMode {
  if (typeof window === 'undefined') return defaultMode;
  try {
    const mode = localStorage.getItem(storageKey);
    if (isSidebarMode(mode)) return mode;
  } catch {
    // Hardened/private browser contexts: fall through to the default.
  }
  return defaultMode;
}

/** Persist the chosen mode and notify listeners. */
export function persistSidebarMode(mode: SidebarMode, storageKey = CONSOLE_SIDEBAR_MODE_KEY) {
  try {
    localStorage.setItem(storageKey, mode);
  } catch {
    // Persistence is best effort; the current document still changes.
  }
  window.dispatchEvent(new CustomEvent('sidebar-state-changed', { detail: { mode } }));
}
